import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { Globe, Loader2, Search } from "lucide-react";

const ETAPA_LABEL = { nominatim: "Localizando a cidade no OpenStreetMap…", overpass: "Buscando bairros da cidade…", comparando: "Comparando com seu cadastro…" };

/**
 * Cabeçalho "Cidade" do cadastro de bairros (Iter 101): informa cidade/UF, dispara a
 * descoberta em segundo plano (Nominatim → Overpass) e abre o dialog quando o job termina.
 */
export function CidadeCard({ onResultado }) {
  const [cidade, setCidade] = useState("");
  const [uf, setUf] = useState("");
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/empresa").then((r) => { setCidade(r.data?.cidade || ""); setUf(r.data?.uf || ""); }).catch(() => {});
    return () => clearTimeout(timer.current);
  }, []);

  const poll = async (tentativa = 0) => {
    try {
      const r = await api.get("/bairros/descobrir");
      setJob(r.data);
      if (r.data.running) { timer.current = setTimeout(poll, 1500); return; }
      if (r.data.status === "error") toast.error(r.data.error || "Falha ao buscar bairros");
      else if (r.data.status === "done") onResultado?.(r.data);
    } catch (e) {
      if (tentativa < 2) { timer.current = setTimeout(() => poll(tentativa + 1), 2000); return; }
      toast.error(e?.response?.data?.detail || "Erro ao consultar a busca"); setJob(null);
    }
  };

  const buscar = async () => {
    if (cidade.trim().length < 2) return toast.error("Informe a cidade");
    try {
      const r = await api.post("/bairros/descobrir", { cidade: cidade.trim(), uf: uf.trim() });
      setJob(r.data);
      timer.current = setTimeout(poll, 1200);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao iniciar a busca"); }
  };

  const running = !!job?.running;
  return (
    <Card className="border-sky-200 bg-sky-50/40" data-testid="card-cidade">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3 flex-wrap">
          <div className="flex items-center gap-2 min-w-[220px]">
            <Globe className="w-5 h-5 text-sky-700 shrink-0" />
            <div>
              <div className="text-sm font-bold text-sky-900">Cidade dos bairros</div>
              <div className="text-[11px] text-sky-800/80">Busca todos os bairros da cidade no OpenStreetMap (grátis, sem chave) e lista para você importar.</div>
            </div>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="text-[10px] font-bold text-muted-foreground uppercase">Cidade</label>
            <Input value={cidade} onChange={(e) => setCidade(e.target.value)} placeholder="Ex.: Anápolis" disabled={running}
              onKeyDown={(e) => e.key === "Enter" && !running && buscar()} data-testid="input-cidade-descoberta" />
          </div>
          <div className="w-20">
            <label className="text-[10px] font-bold text-muted-foreground uppercase">UF</label>
            <Input value={uf} onChange={(e) => setUf(e.target.value.toUpperCase().slice(0, 2))} placeholder="GO" maxLength={2} disabled={running}
              className="uppercase" data-testid="input-uf-descoberta" />
          </div>
          <div className="self-end">
            <Button onClick={buscar} disabled={running} className="bg-sky-700 hover:bg-sky-800" data-testid="btn-buscar-bairros-cidade">
              {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
              {running ? "Buscando…" : "Buscar bairros da cidade"}
            </Button>
          </div>
          {job?.status === "done" && !running && (
            <Button variant="outline" onClick={() => onResultado?.(job)} className="self-end border-sky-300 text-sky-800" data-testid="btn-reabrir-resultado">
              Ver resultado ({job.total})
            </Button>
          )}
        </div>
        {running && (
          <div className="mt-3 text-xs text-sky-800 flex items-center gap-2" data-testid="descoberta-progresso">
            <Loader2 className="w-3.5 h-3.5 animate-spin" />{ETAPA_LABEL[job?.etapa] || "Processando…"}
            <span className="text-sky-700/70">— roda em segundo plano, respeitando 1 requisição/segundo do OpenStreetMap.</span>
          </div>
        )}
        {job?.status === "error" && !running && (
          <div className="mt-3 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-1.5" data-testid="descoberta-erro">{job.error}</div>
        )}
      </CardContent>
    </Card>
  );
}
